import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWine, getWineRatings, rateWine, getVineyard, addFavorite, removeFavorite, checkFavorite, deleteWine, addToCellar } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { WineScore } from '../components/StarRating';
import { ShareButtons } from '../components/ShareButtons';
import { WinePriceHistory } from '../components/WinePriceHistory';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, Edit, Trash2, Wine, MapPin, Grape, Calendar,
  Droplets, Sparkles, UtensilsCrossed, User, Image as ImageIcon, Layers
} from 'lucide-react';

const WineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isApproved, user, loading: authLoading } = useAuth();
  const [wine, setWine] = useState(null);
  const [vineyard, setVineyard] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userRating, setUserRating] = useState(50);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showCellarDialog, setShowCellarDialog] = useState(false);
  const [cellarForm, setCellarForm] = useState({ quantity: 1, drink_by_year: '', storage_location: '', purchase_price: '' });
  const [addingToCellar, setAddingToCellar] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [wineData, ratingsData] = await Promise.all([
        getWine(id),
        getWineRatings(id),
      ]);
      setWine(wineData);
      setRatings(ratingsData);

      if (wineData.vineyard_id) {
        try {
          const vineyardData = await getVineyard(wineData.vineyard_id);
          setVineyard(vineyardData);
        } catch (e) {
          console.error('Failed to load vineyard:', e);
        }
      }

      if (isAuthenticated) {
        const favStatus = await checkFavorite(id);
        setIsFavorite(favStatus);
        const existingRating = ratingsData.find(r => r.user_id === user?.id);
        if (existingRating) {
          setUserRating(existingRating.rating);
          setRatingComment(existingRating.comment);
        }
      }
    } catch (error) {
      console.error('Failed to load wine:', error);
      toast.error('Failed to load wine');
      navigate('/wines');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, user?.id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favorites');
      return;
    }
    try {
      if (isFavorite) {
        await removeFavorite(id);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await addFavorite(id, 'wine');
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleRate = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to rate');
      return;
    }
    if (!isApproved) {
      toast.error('Your account is pending admin approval');
      return;
    }
    setSubmittingRating(true);
    try {
      await rateWine(id, { wine_id: id, rating: userRating, comment: ratingComment });
      toast.success('Rating submitted');
      loadData();
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteWine(id);
      toast.success('Wine deleted');
      navigate('/wines');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleAddToCellar = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add to cellar');
      return;
    }
    if (!isApproved) {
      toast.error('Your account is pending admin approval');
      return;
    }
    setAddingToCellar(true);
    try {
      await addToCellar({
        wine_id: id,
        quantity: parseInt(cellarForm.quantity) || 1,
        drink_by_year: cellarForm.drink_by_year ? parseInt(cellarForm.drink_by_year) : null,
        storage_location: cellarForm.storage_location || '',
        purchase_price: cellarForm.purchase_price ? parseFloat(cellarForm.purchase_price) : null,
      });
      toast.success('Added to your cellar');
      setShowCellarDialog(false);
      setCellarForm({ quantity: 1, drink_by_year: '', storage_location: '', purchase_price: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add to cellar');
    } finally {
      setAddingToCellar(false);
    }
  };

  const getScoreLabel = (score) => {
    if (score >= 95) return 'Exceptional';
    if (score >= 90) return 'Outstanding';
    if (score >= 85) return 'Very Good';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Average';
    return 'Below Average';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  if (!wine) return null;

  const isOwner = user?.id === wine.created_by || user?.is_admin;

  return (
    <div data-testid="wine-detail-page">
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Back & Actions */}
        <div className="flex justify-between items-center mb-8">
          <Link
            to="/wines"
            className="flex items-center gap-2 text-stone-500 hover:text-stone-300 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Wines
          </Link>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleFavorite}
              className={`rounded-full ${isFavorite ? 'text-red-500' : 'text-stone-400'}`}
              data-testid="favorite-btn"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
            </Button>
            {isAuthenticated && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCellarDialog(true)}
                className="rounded-full text-stone-400 hover:text-[#d4af37]"
                data-testid="add-to-cellar-btn"
                title="Add to my cellar"
              >
                <Layers className="w-5 h-5" />
              </Button>
            )}
            <ShareButtons title={wine?.name} testId="wine-share-btn" />
            {isOwner && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/wines/${id}/edit`)}
                  className="rounded-full text-stone-400"
                  data-testid="edit-btn"
                >
                  <Edit className="w-5 h-5" />
                </Button>
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full text-red-400"
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-stone-900 border-stone-700">
                    <DialogHeader>
                      <DialogTitle className="text-stone-50">Delete Wine</DialogTitle>
                    </DialogHeader>
                    <p className="text-stone-400">
                      Are you sure you want to delete "{wine.name}"? This action cannot be undone.
                    </p>
                    <div className="flex gap-4 mt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteDialog(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        className="flex-1"
                        data-testid="confirm-delete-btn"
                      >
                        Delete
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Hero Image — wine photo or vineyard fallback */}
        {(wine.photos?.[0] || vineyard?.photos?.[0]) && (
          <div
            className="relative w-full h-64 md:h-80 mb-10 overflow-hidden border border-white/5"
            data-testid="wine-hero-image"
          >
            <img
              src={wine.photos?.[0] || vineyard.photos[0]}
              alt={wine.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-transparent" />
            {!wine.photos?.[0] && vineyard?.photos?.[0] && (
              <span className="absolute bottom-3 right-3 px-2 py-1 bg-stone-950/70 backdrop-blur-sm border border-white/10 text-[10px] uppercase tracking-widest text-stone-400">
                Estate photo
              </span>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-8">
            {/* Title Section */}
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] mb-3">
                <Grape className="w-4 h-4" />
                {wine.grape_variety}
              </div>
              <h1 className="text-4xl md:text-5xl font-serif text-stone-50 mb-4">
                {wine.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-stone-500">
                {vineyard && (
                  <Link
                    to={`/vineyards/${vineyard.id}`}
                    className="flex items-center gap-2 hover:text-[#d4af37] transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    {vineyard.name}
                  </Link>
                )}
                {wine.vintage && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Vintage {wine.vintage}
                  </span>
                )}
              </div>
            </div>

            {/* Score */}
            <div className="flex items-center gap-6 p-6 bg-stone-900/30 border border-white/5">
              <WineScore score={wine.average_rating || 50} size="lg" />
              <div>
                <p className="text-2xl font-serif text-stone-50">
                  {getScoreLabel(wine.average_rating || 50)}
                </p>
                <p className="text-stone-500">
                  {wine.rating_count} {wine.rating_count === 1 ? 'rating' : 'ratings'}
                </p>
              </div>
            </div>

            {/* Description */}
            {wine.description && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4">About this Wine</h3>
                <p className="text-stone-400 leading-relaxed">{wine.description}</p>
              </div>
            )}

            {/* Tasting Notes */}
            {(wine.tasting_notes || wine.aroma || wine.flavor || wine.finish) && (
              <div className="space-y-6">
                <h3 className="text-xs uppercase tracking-widest text-[#d4af37]">Tasting Characteristics</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {wine.aroma && (
                    <div className="p-5 bg-stone-900/20 border border-white/5">
                      <div className="flex items-center gap-2 text-stone-500 mb-2">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-widest">Aroma</span>
                      </div>
                      <p className="text-stone-300">{wine.aroma}</p>
                    </div>
                  )}
                  
                  {wine.flavor && (
                    <div className="p-5 bg-stone-900/20 border border-white/5">
                      <div className="flex items-center gap-2 text-stone-500 mb-2">
                        <Droplets className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-widest">Flavor</span>
                      </div>
                      <p className="text-stone-300">{wine.flavor}</p>
                    </div>
                  )}
                  
                  {wine.finish && (
                    <div className="p-5 bg-stone-900/20 border border-white/5">
                      <div className="flex items-center gap-2 text-stone-500 mb-2">
                        <Wine className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-widest">Finish</span>
                      </div>
                      <p className="text-stone-300">{wine.finish}</p>
                    </div>
                  )}
                  
                  {wine.tasting_notes && (
                    <div className="p-5 bg-stone-900/20 border border-white/5 md:col-span-2">
                      <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                        Tasting Notes
                      </div>
                      <p className="text-stone-300">{wine.tasting_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Food Pairings */}
            {wine.food_pairings?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4 flex items-center gap-2">
                  <UtensilsCrossed className="w-4 h-4" /> Food Pairings
                </h3>
                <div className="flex flex-wrap gap-2">
                  {wine.food_pairings.map((pairing, index) => (
                    <span
                      key={`pairing-${pairing}-${index}`}
                      className="px-4 py-2 bg-stone-900/30 border border-white/5 text-stone-400 text-sm"
                    >
                      {pairing}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Photos */}
            {wine.photos?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Photos
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {wine.photos.map((photo, index) => (
                    <button
                      key={`photo-${photo}-${index}`}
                      onClick={() => setSelectedPhoto(photo)}
                      className="aspect-square overflow-hidden bg-stone-900 border border-white/5 hover:border-[#d4af37]/30 transition-colors"
                      data-testid={`photo-${index}`}
                    >
                      <img
                        src={photo}
                        alt={`${wine.name} photo ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Price History */}
            <WinePriceHistory
              wineId={wine.id}
              isAdmin={!!user?.is_admin}
              isAuthenticated={isAuthenticated}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-5 space-y-6">
            {/* Rate Wine */}
            {isAuthenticated && (
              <div className="glass-panel p-6 space-y-6">
                <h3 className="text-lg font-serif text-stone-50">Rate this Wine</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-500">Your Score</span>
                    <span className="text-2xl font-serif text-[#d4af37]">{userRating}</span>
                  </div>
                  <Slider
                    value={[userRating]}
                    onValueChange={(v) => setUserRating(v[0])}
                    min={1}
                    max={100}
                    step={1}
                    className="py-2"
                    data-testid="rating-slider"
                  />
                  <div className="flex justify-between text-xs text-stone-600">
                    <span>1</span>
                    <span>50</span>
                    <span>100</span>
                  </div>
                </div>
                <Textarea
                  placeholder="Share your tasting notes (optional)"
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  className="bg-stone-900/50 border-stone-700"
                  rows={3}
                  data-testid="rating-comment"
                />
                <Button
                  onClick={handleRate}
                  disabled={submittingRating}
                  className="w-full bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none"
                  data-testid="submit-rating-btn"
                >
                  {submittingRating ? 'Submitting...' : 'Submit Rating'}
                </Button>
              </div>
            )}

            {/* Wine Details */}
            <div className="glass-panel p-6 space-y-4">
              <h3 className="text-xs uppercase tracking-widest text-[#d4af37]">Details</h3>
              
              {wine.alcohol_content && (
                <div className="flex justify-between py-3 border-b border-white/5">
                  <span className="text-stone-500">Alcohol</span>
                  <span className="text-stone-300">{wine.alcohol_content}%</span>
                </div>
              )}
              
              {wine.price_range && (
                <div className="flex justify-between py-3 border-b border-white/5">
                  <span className="text-stone-500">Price Range</span>
                  <span className="text-stone-300">{wine.price_range}</span>
                </div>
              )}
              
              <div className="flex justify-between py-3">
                <span className="text-stone-500">Grape</span>
                <span className="text-stone-300">{wine.grape_variety}</span>
              </div>
            </div>

            {/* Reviews */}
            <div className="glass-panel p-6">
              <h3 className="text-xs uppercase tracking-widest text-[#d4af37] mb-4">
                Recent Reviews ({ratings.length})
              </h3>
              
              {ratings.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {ratings.slice(0, 5).map((rating) => (
                    <div key={rating.id} className="p-4 bg-stone-900/30 border border-white/5" data-testid={`review-${rating.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center">
                            <User className="w-4 h-4 text-stone-500" />
                          </div>
                          <div>
                            <p className="text-stone-300 text-sm">{rating.user_name}</p>
                            <p className="text-xs text-stone-600">
                              {new Date(rating.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <WineScore score={rating.rating} size="sm" />
                      </div>
                      {rating.comment && (
                        <p className="text-stone-400 text-sm mt-2">{rating.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-stone-500 text-center py-4">No reviews yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-4xl bg-stone-950 border-stone-800 p-0">
            <img
              src={selectedPhoto}
              alt="Gallery"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Add to Cellar Dialog */}
      <Dialog open={showCellarDialog} onOpenChange={setShowCellarDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 max-w-md" data-testid="add-cellar-dialog">
          <DialogHeader>
            <DialogTitle className="text-stone-50 font-serif flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#d4af37]" /> Add to Your Cellar
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500">
            Adding <span className="text-stone-300">{wine.name}</span>
            {wine.vintage && <span className="text-stone-500"> ({wine.vintage})</span>}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-stone-400">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={cellarForm.quantity}
                onChange={(e) => setCellarForm((f) => ({ ...f, quantity: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
                data-testid="cellar-dialog-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-400">Drink By Year</Label>
              <Input
                type="number"
                min="1900"
                max="2100"
                placeholder="e.g. 2030"
                value={cellarForm.drink_by_year}
                onChange={(e) => setCellarForm((f) => ({ ...f, drink_by_year: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-400">Location</Label>
              <Input
                placeholder="e.g. Rack B3"
                value={cellarForm.storage_location}
                onChange={(e) => setCellarForm((f) => ({ ...f, storage_location: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-stone-400">Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g. 45.00"
                value={cellarForm.purchase_price}
                onChange={(e) => setCellarForm((f) => ({ ...f, purchase_price: e.target.value }))}
                className="bg-stone-900/50 border-stone-700"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCellarDialog(false)}
              className="flex-1 border-stone-700 rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToCellar}
              disabled={addingToCellar}
              className="flex-1 bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
              data-testid="cellar-dialog-submit"
            >
              {addingToCellar ? 'Adding…' : 'Add Bottle'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WineDetail;
